import { makeStyles } from '@material-ui/styles'
import FormFieldSection from 'core/components/validatedForm/FormFieldSection'
import useListAction from 'core/hooks/useListAction'
import useParams from 'core/hooks/useParams'
import { Route } from 'core/plugins/route'
import Theme from 'core/themes/model'
import { routeManager } from 'core/utils/routes'
import React, { useCallback, useEffect, useState } from 'react'
import useReactRouter from 'use-react-router'
import HelpContainer from 'core/components/HelpContainer'
import PicklistField from 'core/components/validatedForm/DropdownField'
import TextField from 'core/components/validatedForm/TextField'
import Grid from 'core/elements/grid'
import GridSearchFilter from 'core/elements/grid/GridSearchFilter'
import { middleLeft, topLeft } from 'core/elements/menu/defaults'
import ModalForm from 'core/elements/modal/ModalForm'
import Text from 'core/elements/Text'
import useSelectorWithParams from 'core/hooks/useSelectorWithParams'
import { minValueValidator } from 'core/utils/fieldValidators'
import ControlPlaneCard from 'k8s/components/cluster/deployment/form-components/ControlPlaneCard'
import { path } from 'ramda'
import { listNetworks } from '../../../networks/actions'
import { networksSelector } from '../../../networks/selectors'
import { IAppsDetailsPageTabs } from '../../model'
import {
  cpuImagePath,
  customAppConfigMessage,
  defaultAppConfigMessage,
  memoryImagePath,
  storageImagePath,
  vmImagePath,
} from '../constant'
import InfoBox from '../InfoBox'
import TemplatetPicklist from '../templates/TemplatePicklist'
import VmSlider from './VmSlider'
import { useAppSelector } from 'app/store'
import { createApplication, listApplications, listTemplates } from '../../repositories/actions'
import { templateSelector } from '../../repositories/selector'
import { getAppRequestBody } from '../helper'
import useUpdateAction from 'core/hooks/useUpdateAction'
// import { loadBalancerDoc } from 'app/docLinks'

interface Props {
  addRoute: Route
  tabName: IAppsDetailsPageTabs
  data?: any
}

const SubnetsCellComponent = ({ value }) => {
  return (
    <>
      {value.map((subnet) => (
        <Text variant="body2" key={subnet.id}>
          {subnet.name}: <b>{subnet.cidr}</b>
        </Text>
      ))}
    </>
  )
}

const networkColumns = [
  {
    key: 'name',
    label: 'Name',
  },
  {
    key: 'subnetDetails',
    label: 'Subnets',
    render: (value) => <SubnetsCellComponent value={value} />,
  },
  {
    key: 'availableIps',
    label: 'Available IPs',
  },
]
const searchTargets = ['name']

const globalFilters = [
  {
    key: 'search',
    equalityComparerFn: (item, value) => {
      return !!searchTargets.find((key) =>
        String(
          Object.prototype.hasOwnProperty.call(item, key)
            ? item[String(key)]
            : path(String(key).split('.'), item),
        )
          .toLocaleLowerCase()
          .includes(value.toLocaleLowerCase()),
      )
    },
    FilterComponent: GridSearchFilter,
  },
]
export default function CreateNewAppModal({ addRoute, tabName, data }: Props) {
  const { history, location } = useReactRouter()
  const imagePath = '/ui/images/coins-storage.svg'
  const classes = useStyles()
  const [attemptedSubmit, setAttemptedSubmit] = useState(false)
  const [selectedNetwork, setSelectedVms] = useState([])
  const searchParams = new URLSearchParams(location.search)
  const templateId = searchParams.get('selectedTemplateId')

  const handleNetworkSelection = (selectedItems) => {
    setSelectedVms(selectedItems)
    // onChange({ flavor: selectedItems[0]?.name, replicas: selectedVmQuantity || 1 })
  }
  const defaultParams = {
    name: '',
    templateId: '',
    creationStrategyType: 'managed',
    description: '',
    vmCount: 4,
    provider: 'ovn',
    vms: '',
    totalCpu: '',
    totalRam: '',
    totalDisk: '',
  }
  const { params, getParamsUpdater, setParams, updateParams } = useParams(defaultParams)
  const { loading: loadingNetworks, reload: reloadNetworks } = useListAction(listNetworks, {
    params: {},
  })
  const allNetworksList = useSelectorWithParams(networksSelector, {} as any)
  const { loading, reload } = useListAction(listTemplates)
  const templateDetails = useAppSelector(templateSelector)

  useEffect(() => {
    if (!params.templateId || !Array.isArray(templateDetails)) return

    const selectedTemplate = templateDetails.find((template) => template.uid === params.templateId)
    if (!selectedTemplate) return

    updateParams({
      vms: selectedTemplate.vms?.toString() || '',
      totalCpu: selectedTemplate.totalVcpus?.toString() || '',
      totalRam: selectedTemplate.totalRam?.toString() || '',
      totalDisk: selectedTemplate.totalDisk?.toString() || '',
    })
  }, [params.templateId, templateDetails])

  const appDetails = [
    {
      label: 'VMs',
      value: params?.vms,
      imagePath: vmImagePath,
      showDivider: true,
    },
    {
      label: 'CPUs',
      value: `${params?.totalCpu} cores`,
      imagePath: cpuImagePath,
      showDivider: true,
    },
    {
      label: 'Storage',
      value: `${params?.totalDisk} GB`,
      imagePath: storageImagePath,
      showDivider: true,
    },
    {
      label: 'Memory',
      value: `${params?.totalRam} GB`,
      imagePath: memoryImagePath,
      showDivider: false,
    },
  ]
  const appNetworkDetails = [
    {
      label: 'Network Configuration',
      value: 'Public + Private Network',
      showDivider: true,
    },
    {
      label: 'Cluster',
      value: 'Cluster-Name-1',
      showDivider: true,
    },
    {
      label: 'Owner',
      value: 'John Doe',
      showDivider: false,
    },
  ]

  //   const { reload } = useListAction(listLoadBalancers, {})
  const { reload: reloadApps } = useListAction(listApplications, {})
  const { update, updating, error, reset } = useUpdateAction(createApplication)

  const createAppFn = async (params) => {
    const body = getAppRequestBody(params)
    const { success, response } = await update({ body })
    return { success, response }
  }

  const handleClose = () => {
    setParams(defaultParams)
    reset()
    history.push(
      routeManager.routes.openstack.appsDetails.details.path({
        tab: tabName,
      }),
    )
  }

  const submitForm = useCallback(async () => {
    setAttemptedSubmit(true)

    const { success } = await createAppFn(params)
    if (success) {
      handleClose()
      reload()
    }
    // handleClose()
    setAttemptedSubmit(false)
  }, [params, createAppFn, handleClose])

  useEffect(() => {
    if (templateId && !params.templateId) {
      updateParams({ templateId })
    }
  }, [templateId, params.templateId])

  useEffect(() => {
    reload(true)
  }, [])

  return (
    <ModalForm
      route={addRoute}
      title={`Create New Application`}
      onSubmit={submitForm}
      onClose={handleClose}
      submitting={updating || attemptedSubmit}
      error={error}
      submitTitle={`Create Application`}
      maxWidth={780}
    >
      <>
        <div className={classes.formFieldSection}>
          <div className={classes.contentContainer}>
            <img className={classes.iconWrapper} alt="Load Balancer" src={imagePath} />
            <div className={classes.content}>
              <Text variant="body2">
                Create a new application using a predefined template. You can either use the default
                settings defined in the template or request more resources for the app.
                {/* <ExternalLink url={loadBalancerDoc}>here.</ExternalLink> */}
              </Text>
            </div>
          </div>
        </div>
        <FormFieldSection className={classes.customFormFieldSection} title="App Configuration">
          <div className={classes.subContainer}>
            <TextField
              id="name"
              label="Application Name"
              onChange={getParamsUpdater('name')}
              value={params?.name}
              info="Enter a unique name for the application instance. This name helps identify and manage the deployed app."
              required
            />
            <PicklistField
              DropdownComponent={TemplatetPicklist}
              className={classes.templateDropdownContainer}
              name="template"
              id={`fixedIps.{idx}.template`}
              label="Template"
              compact={false}
              value={params?.templateId}
              onChange={getParamsUpdater('templateId')}
              info="Choose an existing template from the App Catalog. This template defines the infrastructure, network, and deployment script."
              required
            />
          </div>
        </FormFieldSection>
        <FormFieldSection
          className={classes.customFormFieldSection}
          title="Creation Strategy"
          info="Choose how you want to configure the application before deployment."
        >
          <div className={classes.subContainer}>
            <ControlPlaneCard
              id="managed"
              onClick={() => getParamsUpdater('creationStrategyType')('managed')}
              active={params?.creationStrategyType === 'managed'}
              title="Use default template properties"
              description=""
            />
            <ControlPlaneCard
              id="custom"
              // onClick={() => getParamsUpdater('creationStrategyType')('custom')}
              onClick={() => {}}
              active={params?.creationStrategyType === 'custom'}
              title="Change template properties"
              description=""
              // info="If you request more resources than what's provided in the template, your app will need to be approved by an admin."
              comingSoon
            />
          </div>
          <div className={classes.subContent}>
            <Text variant="body2">
              {params?.creationStrategyType === 'managed'
                ? defaultAppConfigMessage
                : customAppConfigMessage}
            </Text>
            {params?.creationStrategyType === 'managed' ? (
              <div className={classes.subContent}>
                <div className={classes.resourceContainer}>
                  {appDetails?.map((item, index) => (
                    <InfoBox
                      label={item?.label}
                      value={item?.value}
                      imagePath={item?.imagePath}
                      showDivider={item?.showDivider}
                    />
                  ))}
                </div>
                {/* <div className={classes.iconsContainer}>
                  {appNetworkDetails.map((item, index) => (
                    <InfoBox
                      label={item?.label}
                      value={item?.value}
                      showDivider={item?.showDivider}
                    />
                  ))}
                </div> */}
              </div>
            ) : (
              <div className={classes.subContent}>
                <div>
                  <div className={classes.iconContainer}>
                    <Text variant="body2">Number of VMs</Text>
                    <HelpContainer
                      title={
                        'Choose the number of VMs to deploy with this template. You can select between 1 and 20 VMs.'
                      }
                    />
                  </div>
                  <VmSlider value={params?.vmCount} onChange={getParamsUpdater('vmCount')} />
                </div>
                <div className={classes.templateContainer}>
                  <TextField
                    id="name"
                    label="CPU per VM (cores)"
                    onChange={getParamsUpdater('name')}
                    value={params.name}
                    info="Select the number of virtual CPUs for each VM in the template."
                    type="number"
                    validations={[minValueValidator(1)]}
                    required
                  />
                  <TextField
                    id="name"
                    label="Memory per VM (GB)"
                    onChange={getParamsUpdater('name')}
                    value={params?.name}
                    info="Specify the memory for each VM."
                    type="number"
                    validations={[minValueValidator(1)]}
                    required
                  />
                  <TextField
                    id="name"
                    label="Storage per VM (GB)"
                    onChange={getParamsUpdater('name')}
                    value={params?.name}
                    info="Set the disk size for each VM."
                    type="number"
                    validations={[minValueValidator(1)]}
                    required
                  />
                </div>
                <div className={classes.iconsContainer}>
                  {appNetworkDetails?.map((item, index) => (
                    <InfoBox
                      label={item?.label}
                      value={item?.value}
                      // imagePath={item?.imagePath}
                      showDivider={item?.showDivider}
                      // classes={classes}
                    />
                  ))}
                </div>
                {/* <div className={classes.formFieldSection}> */}
                <Grid
                  uniqueIdentifier="id"
                  columns={networkColumns}
                  data={allNetworksList}
                  label="Networks"
                  orderBy="name"
                  orderDirection="asc"
                  emptyContent="No Networks available"
                  disableRowSelection={false}
                  multiSelection={true}
                  selectedItems={selectedNetwork}
                  onSelectChange={handleNetworkSelection}
                  onRefresh={reloadNetworks}
                  loading={loadingNetworks}
                  globalFilters={globalFilters}
                  disableColumnHiding
                />
              </div>
              // </div>
            )}
          </div>
        </FormFieldSection>

        {/* <FormFieldSection className={classes.customFormFieldSection} title="Upload App Icon">
          <ImageUpload
            id="image-upload"
            fileTypes={['.jpg', '.png']}
            onChange={(file) => {
              console.log('Uploaded file:', file)
              // Handle uploaded file logic here
            }}
            onChangeName={(name) => {
              console.log('File name:', name)
              // Optional: Handle name change
            }}
          />
        </FormFieldSection> */}
      </>
    </ModalForm>
  )
}

const useStyles = makeStyles<Theme>(() => ({
  formFieldSection: {
    marginBottom: '32px',
    border: 'none',
    padding: 0,
  },
  content: {
    display: 'grid',
    gap: 16,
    gridAutoRows: 'max-content',
  },
  contentContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
  },
  iconWrapper: {
    display: 'flex',
    height: '32px',
    width: '32px',
    borderRadius: '50%',
    justifyContent: 'center',
    alignItems: 'center',
  },
  customFormFieldSection: {
    '& .content': {
      marginLeft: '0px',
    },
  },
  subContainer: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    alignItems: 'baseline',
    // paddingLeft: 8,
    gap: 16,
    '& .coming-soon': {
      padding: '5px 10px 6px 16px',
    },
  },
  templateContainer: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    alignItems: 'center',
    // paddingLeft: 8,
    gap: 16,
  },
  iconsContainer: {
    display: 'flex',
    gap: 32,
    alignItems: 'center',
  },
  subContent: {
    display: 'grid',
    gap: 24,
    gridAutoRows: 'max-content',
  },
  iconContainer: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
  },
  resourceContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  templateDropdownContainer: {
    '& .dropdownMenu': {
      zIndex: 11,
    },
  },
}))
